import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { DropdownContext } from 'components/DropdownContext';
import { RequestStatus } from 'features/request/requestSlice';
import type { CatalogUerOtherCostTemplateData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';

import { useCallback, useMemo } from 'react';

import { useDownloadOtherCostTemplate } from './hooks/useDownloadOtherCostTemplate';
import { useRemoveOtherCostTemplates } from './hooks/useRemoveOtherCostTemplates';

import Styles from './catalogOtherCostTemplates.module.css';

interface IRowMenu {
    data: CatalogUerOtherCostTemplateData;
    createOrEditModalHandler: (state: boolean) => void;
    setOpenedTemplateId: React.Dispatch<React.SetStateAction<number | null>>;
    onRemoveSuccess: () => void;
    catalogOtherCostTemplatesRules: IUseAccess['catalogOtherCostTemplates'];
}

export const RowMenu: React.FC<IRowMenu> = ({
    data,
    createOrEditModalHandler,
    setOpenedTemplateId,
    onRemoveSuccess,
    catalogOtherCostTemplatesRules,
}) => {
    const { catalogRemoveOtherCostTemplatesRequest, catalogRemoveOtherCostTemplatesStatus } =
        useRemoveOtherCostTemplates([data.id], onRemoveSuccess);

    const { onDownloadOtherCostTemplate, downloadOtherCostTemplateStatus } = useDownloadOtherCostTemplate(data.id);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => catalogRemoveOtherCostTemplatesRequest(),
        });

    const isDisabledDeleteButton = catalogRemoveOtherCostTemplatesStatus === RequestStatus.FETCHING;
    const isDisabledDownloadButton = downloadOtherCostTemplateStatus === RequestStatus.FETCHING;

    const onOpenEditModalHandler = useCallback(() => {
        createOrEditModalHandler(true);
        setOpenedTemplateId(data.id);
    }, [createOrEditModalHandler, data.id, setOpenedTemplateId]);

    const downloadTemplateHandler = useCallback(() => {
        onDownloadOtherCostTemplate(data.id);
    }, [data.id, onDownloadOtherCostTemplate]);

    const items = useMemo(
        () => [
            {
                label: 'Скачать',
                action: downloadTemplateHandler,
                disabled: isDisabledDownloadButton || !catalogOtherCostTemplatesRules.canDelete,
            },
            {
                label: 'Редактировать',
                action: onOpenEditModalHandler,
                disabled: !catalogOtherCostTemplatesRules.canEdit,
            },
            {
                label: 'Удалить',
                haveTooltip: isDisabledDeleteButton,
                disabled: isDisabledDeleteButton || !catalogOtherCostTemplatesRules.canDelete,
                action: handleOpenConfirmationDialog,
            },
        ],
        [
            catalogOtherCostTemplatesRules.canDelete,
            catalogOtherCostTemplatesRules.canEdit,
            downloadTemplateHandler,
            handleOpenConfirmationDialog,
            isDisabledDeleteButton,
            isDisabledDownloadButton,
            onOpenEditModalHandler,
        ],
    );

    const { getMenuItemAttributes, onMenuMouseMove, onMenuMouseLeave } = useDropdownContextMenuTooltipController();

    return (
        <>
            <DropdownContext
                getItemAttributesHandler={getMenuItemAttributes}
                onMouseMoveHandler={onMenuMouseMove}
                onMouseLeaveHandler={onMenuMouseLeave}
                items={items}
                direction="downStartRight"
                wrapperClassName={Styles.wrapperDropdown}
                isDisabled={!catalogOtherCostTemplatesRules.canEdit || !catalogOtherCostTemplatesRules.canDelete}
            />
            <ConfirmationDialog
                textHeader="удаление записи"
                textConfirmation="Вы уверены, что хотите удалить запись?"
                confirmButtonText="Удалить"
                cancelButtonText="Не удалять"
                onSubmit={handleActionQuery}
                isOpened={isOpenConfirmationDialog}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

RowMenu.displayName = 'RowMenu';
